import { useCallback, useEffect, useState } from "react";
import { Loader2, X, Check, Pencil, Ban, HelpCircle, Info, AlertTriangle, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi, money } from "./lib/api";

const CHOICE_ICON = { approve: Check, modify: Pencil, no_billing: Ban, waiting: HelpCircle };

/** FIN-2 — one billing record, end to end: what we suggest, what the PM decided, what
 *  accounting did with that, the notes thread and every move that was made. */
export default function BillingReviewDrawer({ row, onClose, onSaved }) {
  const [data, setData] = useState(null);
  const [choice, setChoice] = useState("approve");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [shown, setShown] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await opsApi.billingRecord(row.project_id, row.period_end);
      setData(d);
      setAmount(String(d.current_amount ?? d.record.suggested_amount ?? 0));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load that billing"); }
  }, [row.project_id, row.period_end]);
  useEffect(() => { load(); }, [load]);

  const submitPm = async () => {
    setBusy(true);
    try {
      const d = await opsApi.billingPmReview(row.project_id, row.period_end, {
        choice, amount: choice === "modify" ? Number(amount) : null, reason, note,
      });
      setData(d); setReason(""); setNote("");
      toast.success("Recorded");
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail
        : detail?.detail || "That could not be recorded");
    } finally { setBusy(false); }
  };

  const submitAccounting = async (decision) => {
    setBusy(true);
    try {
      const d = await opsApi.billingAccountantReview(row.project_id, row.period_end,
        { decision, note });
      setData(d); setNote("");
      toast.success(decision === "approve" ? "Approved" : "Sent back for adjustment");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "That could not be recorded"); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      setData(await opsApi.billingRecordNote(row.project_id, row.period_end, note));
      setNote("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add that note"); }
    finally { setBusy(false); }
  };

  const rec = data?.record;
  const th = data?.thresholds;
  const delta = rec ? Number(amount || 0) - Number(rec.suggested_amount || 0) : 0;
  const material = rec && (!Number(rec.suggested_amount)
    || Math.abs(delta) > Number(th?.amount || 0)
    || Math.abs(delta) / rec.suggested_amount > Number(th?.pct || 0));

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40" onClick={onClose}>
      <div className="h-full w-full max-w-2xl overflow-y-auto bg-white shadow-2xl"
           onClick={(e) => e.stopPropagation()} data-testid="billing-review-drawer">
        <div className="sticky top-0 z-10 flex items-start gap-3 border-b border-slate-200 bg-white px-6 py-4">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
              {row.period_label} billing
            </p>
            <h2 className="truncate text-lg font-semibold text-slate-900"
                data-testid="billing-review-title">
              {row.job_number ? `${row.job_number} — ` : ""}{row.name}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {row.gc || "No GC on file"} · {row.platform || "no platform"} · GC wants it {row.gc_due}
            </p>
          </div>
          <button onClick={onClose} data-testid="billing-review-close"
                  className="ml-auto rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        </div>

        {!data ? (
          <div className="flex items-center gap-2 px-6 py-16 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading the billing record…
          </div>
        ) : (
          <div className="space-y-5 px-6 py-5">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[["Contract", money(data.project.contract_value)],
                ["Previously billed", money(data.project.previously_billed)],
                ["Suggested", money(rec.suggested_amount)],
                ["On the table", money(data.current_amount)]].map(([k, v]) => (
                <div key={k} className="rounded-lg border border-slate-200 px-3 py-2">
                  <p className="text-[11px] text-slate-500">{k}</p>
                  <p className="text-sm font-semibold text-slate-900">{v}</p>
                </div>
              ))}
            </div>

            {rec.suggested_blockers?.length > 0 && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2"
                   data-testid="billing-blockers">
                <p className="flex items-center gap-1.5 text-xs font-semibold text-rose-900">
                  <AlertTriangle className="h-3.5 w-3.5" /> Not ready to bill
                </p>
                <ul className="mt-1 space-y-0.5 text-xs text-rose-900">
                  {rec.suggested_blockers.map((b, i) => <li key={i}>· {b}</li>)}
                </ul>
              </div>
            )}

            {rec.suggested_warnings?.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                   data-testid="billing-warnings">
                {rec.suggested_warnings.map((w, i) => <p key={i}>· {w}</p>)}
              </div>
            )}

            {data.suggested_is_placeholder && !rec.suggested_blockers?.length && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                   data-testid="billing-suggestion-placeholder">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  <strong>No engine suggestion for this period.</strong> This record was frozen
                  before the suggested billing engine went in, so the figure is the last billing
                  carried forward. Check it against the job before you approve.
                </span>
              </div>
            )}

            {rec.suggested_breakdown?.length > 0 && (
              <div className="rounded-lg border border-slate-200" data-testid="billing-breakdown">
                <p className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  How that was worked out — everything expected through {rec.suggested_as_of}
                </p>
                {rec.suggested_breakdown.map((b, i) => (
                  <div key={i} className="flex justify-between px-3 py-1.5 text-xs">
                    <span className="text-slate-600">{b.label}</span>
                    <span className="font-medium">{money(b.amount)}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-slate-100 px-3 py-1.5 text-xs font-semibold">
                  <span>Suggested current billing</span>
                  <span>{money(rec.suggested_amount)}</span>
                </div>
              </div>
            )}

            {rec.suggested_lines?.filter((l) => l.work_this_period || l.stored_materials).length > 0 && (
              <div className="rounded-lg border border-slate-200" data-testid="billing-line-math">
                <p className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Line by line
                </p>
                {rec.suggested_lines.filter((l) => l.work_this_period || l.stored_materials)
                  .map((l) => (
                  <div key={l.sov_line_id} className="border-b border-slate-100 px-3 py-2 last:border-0">
                    <button onClick={() => setShown(shown === l.sov_line_id ? "" : l.sov_line_id)}
                            data-testid={`billing-line-${l.cost_code}`}
                            className="flex w-full items-center gap-2 text-left">
                      <ChevronRight className={`h-3 w-3 shrink-0 text-slate-400 transition-transform ${
                        shown === l.sov_line_id ? "rotate-90" : ""}`} />
                      <span className="min-w-0 flex-1 truncate text-xs font-medium text-slate-800">
                        {l.description}
                      </span>
                      <span className="text-[11px] text-slate-500">
                        {Math.round(l.pct_complete * 100)}%
                      </span>
                      <span className="w-24 text-right text-xs font-semibold">
                        {money(l.work_this_period + l.stored_delta)}
                      </span>
                    </button>
                    {shown === l.sov_line_id && (
                      <div className="mt-1.5 space-y-0.5 pl-5">
                        {l.components.map((c, i) => (
                          <div key={i} className="flex justify-between text-[11px]">
                            <span className="text-slate-500">{c.label}</span>
                            <span className="text-slate-700">{money(c.amount)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between border-t border-slate-100 pt-1 text-[11px]">
                          <span className="text-slate-500">
                            Scheduled value {money(l.scheduled_value)} · already billed{" "}
                            {money(l.previous_billed)}
                          </span>
                          <span className="font-medium">
                            work {money(l.work_this_period)} · stored {money(l.stored_materials)}
                          </span>
                        </div>
                        <p className="pt-1 text-[11px] italic text-slate-500">{l.suggestion}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {rec.suggested_not_billable?.length > 0 && (
              <div className="rounded-lg border border-slate-200" data-testid="billing-not-billable">
                <p className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Not billable this period
                </p>
                {rec.suggested_not_billable.slice(0, 12).map((n, i) => (
                  <div key={i} className="flex flex-wrap gap-x-2 px-3 py-1.5 text-[11px]">
                    <span className="font-mono text-slate-500">{n.po_number}</span>
                    <span className="min-w-0 flex-1 truncate text-slate-700">{n.description}</span>
                    <span className="rounded bg-slate-100 px-1.5 text-slate-600">
                      {n.billable_label}
                    </span>
                    <span className="w-20 text-right font-medium">{money(n.value)}</span>
                    <span className="w-full text-slate-400">{n.why}</span>
                  </div>
                ))}
              </div>
            )}

            {rec.suggested_pending_cos?.length > 0 && (
              <div className="rounded-lg border border-slate-200 px-3 py-2 text-[11px]"
                   data-testid="billing-pending-cos">
                <p className="font-semibold uppercase tracking-wide text-slate-500">
                  Change orders pending execution — not billable
                </p>
                {rec.suggested_pending_cos.map((c, i) => (
                  <p key={i} className="mt-0.5 text-slate-600">
                    {c.number} {c.title} — {c.status.replace(/_/g, " ")}
                  </p>
                ))}
              </div>
            )}

            <div className="rounded-lg border border-slate-200 px-4 py-3">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold text-slate-900">Project manager</p>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600"
                      data-testid="billing-review-status">{data.status_label}</span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                Every job needs an answer each cycle. "No billing this month" is an answer.
              </p>

              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {data.pm_choices.map((c) => {
                  const Icon = CHOICE_ICON[c.key];
                  return (
                    <button key={c.key} onClick={() => setChoice(c.key)}
                            data-testid={`billing-choice-${c.key}`}
                            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs font-medium transition-colors ${
                              choice === c.key
                                ? "border-blue-500 bg-blue-50 text-blue-900"
                                : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                      <Icon className="h-3.5 w-3.5 shrink-0" /> {c.label}
                    </button>
                  );
                })}
              </div>

              {choice === "modify" && (
                <div className="mt-3 space-y-2">
                  <label className="block text-xs">
                    <span className="text-slate-500">Amount to bill this period</span>
                    <Input type="number" step="0.01" value={amount}
                           onChange={(e) => setAmount(e.target.value)}
                           className="mt-1 h-9" data-testid="billing-modify-amount" />
                  </label>
                  {material && (
                    <p className="text-xs text-amber-800" data-testid="billing-material-warning">
                      {Number(rec.suggested_amount)
                        ? `That is ${money(Math.abs(delta))} away from the suggestion — over the
                           ${Math.round(Number(th.pct) * 100)}% / ${money(th.amount)} threshold, so a
                           reason is required.`
                        : "There is no suggested amount to compare against yet, so a reason is required."}
                    </p>
                  )}
                </div>
              )}

              {(choice === "modify" || choice === "no_billing" || choice === "waiting") && (
                <label className="mt-2 block text-xs">
                  <span className="text-slate-500">
                    Reason{material && choice === "modify" ? " (required)" : ""}
                  </span>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                            rows={2} className="mt-1 text-xs"
                            data-testid="billing-review-reason" />
                </label>
              )}

              <div className="mt-3 flex justify-end">
                <Button size="sm" onClick={submitPm} disabled={busy}
                        data-testid="billing-review-submit">
                  {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                  Record the decision
                </Button>
              </div>
            </div>

            {data.is_accounting && (
              <div className="rounded-lg border border-slate-200 px-4 py-3"
                   data-testid="billing-accounting-panel">
                <p className="text-sm font-semibold text-slate-900">Accounting</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {rec.pm_by
                    ? `${rec.pm_by} put ${money(data.current_amount)} forward on ${(rec.pm_at || "").slice(0, 10)}.`
                    : "The PM has not answered yet."}
                </p>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={() => submitAccounting("approve")} disabled={busy}
                          data-testid="billing-accounting-approve">Approve</Button>
                  <Button size="sm" variant="outline" disabled={busy}
                          onClick={() => submitAccounting("needs_adjustment")}
                          data-testid="billing-accounting-adjust">Needs adjustment</Button>
                </div>
              </div>
            )}

            <div className="rounded-lg border border-slate-200 px-4 py-3">
              <p className="text-sm font-semibold text-slate-900">Notes</p>
              <div className="mt-2 space-y-2" data-testid="billing-notes">
                {(rec.notes || []).length === 0 && (
                  <p className="text-xs text-slate-500">No notes on this billing yet.</p>
                )}
                {(rec.notes || []).map((n) => (
                  <div key={n.id} className="rounded-md bg-slate-50 px-3 py-2 text-xs">
                    <p className="text-slate-800">{n.text}</p>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      {n.by} · {(n.at || "").slice(0, 16).replace("T", " ")}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-2 flex gap-2">
                <Input value={note} onChange={(e) => setNote(e.target.value)}
                       placeholder="Add a note" className="h-8 text-xs"
                       data-testid="billing-note-input" />
                <Button size="sm" variant="outline" onClick={addNote} disabled={busy || !note.trim()}
                        data-testid="billing-note-add">Add</Button>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200">
              <p className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Audit
              </p>
              <div data-testid="billing-audit">
                {(rec.audit || []).map((a, i) => (
                  <div key={i} className="flex flex-wrap gap-2 px-3 py-1.5 text-[11px] text-slate-600">
                    <span className="font-mono text-slate-400">
                      {(a.at || "").slice(0, 16).replace("T", " ")}
                    </span>
                    <span className="font-medium text-slate-800">{a.what}</span>
                    {a.to && <span>→ {a.to}</span>}
                    {a.amount != null && <span>{money(a.amount)}</span>}
                    <span className="ml-auto text-slate-400">{a.by}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
